/**
 * Utility functions for formatting numbers, currencies, and percentages
 */

/**
 * Format a number as currency (USD)
 */
export function formatCurrency(
  value: number | undefined | null,
  options: {
    decimals?: number;
    compact?: boolean;
  } = {}
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '-';
  }

  const { decimals = 0, compact = false } = options;

  if (compact) {
    const absValue = Math.abs(value);
    if (absValue >= 1e9) {
      return `$${(value / 1e9).toFixed(1)}B`;
    }
    if (absValue >= 1e6) {
      return `$${(value / 1e6).toFixed(1)}M`;
    }
    if (absValue >= 1e3) {
      return `$${(value / 1e3).toFixed(1)}K`;
    }
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a number with thousands separators
 */
export function formatNumber(
  value: number | undefined | null,
  options: {
    decimals?: number;
    compact?: boolean;
  } = {}
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '-';
  }

  const { decimals = 0, compact = false } = options;

  if (compact) {
    const absValue = Math.abs(value);
    if (absValue >= 1e9) {
      return `${(value / 1e9).toFixed(1)}B`;
    }
    if (absValue >= 1e6) {
      return `${(value / 1e6).toFixed(1)}M`;
    }
    if (absValue >= 1e3) {
      return `${(value / 1e3).toFixed(1)}K`;
    }
  }

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a number as a percentage
 */
export function formatPercent(
  value: number | undefined | null,
  decimals: number = 1
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '-';
  }

  return `${value.toFixed(decimals)}%`;
}

/**
 * Format a date
 */
export function formatDate(
  date: Date | string | undefined | null,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }
): string {
  if (!date) {
    return '-';
  }

  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', options).format(d);
}

/**
 * Format a number as a multiple (e.g., 2.5x)
 */
export function formatMultiple(
  value: number | undefined | null,
  decimals: number = 1
): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '-';
  }

  return `${value.toFixed(decimals)}x`;
}

/**
 * Clamp a number between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
